import React, { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useDispatch } from 'react-redux'
import Card from '../../../../components/common/Card/index'
import Typography from '../../../../components/common/Typography/index'
import Button from '../../../../components/common/Button/index'
import useAgent from '../../../../features/agent/hooks/useAgent'
import { fetchPropertiesbyAgent } from '../../../../state/redux/actions/propertyActions'
import useAppSelector from '../../../../state/redux/hooks/index'
import LoadingIndicator from '../../../../components/state/LoadingIndicator'
import ErrorState from '../../../../components/state/ErrorState'
import Backdrop from '../../../../components/common/Backdrop/Backdrop'
import FeedbackPopup from '../../../../components/common/Feedback/Feedbackpopup'
import { ListItemText, ListItemAvatar, Avatar } from '@mui/material'
import { IoCloudDownloadOutline } from 'react-icons/io5'
import { RiDownloadCloud2Fill } from 'react-icons/ri'
import { MdOutlineVerified, MdOutlineArrowDropDown } from 'react-icons/md'
import api from '../../../../shared/utils/api'

import MiniHeader from '../../../../components/layout/MiniHeader'
import AgentActivityTabs from './Tabs'
import { deactivateAgentAccount } from '../../../../state/redux/actions/agentActions'
import { getRecentActivities, getGdprConsents } from '../../../../state/redux/actions/activityActions'

const AgentProfile = () => {
  const { agentId } = useParams()
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const [currentPage, setCurrentPage] = useState(1)
  const [value, setSelectValue] = useState('send')
  const [disapproveToastOpen, setDisapproveToastOpen] = useState(false)
  const [validateToastOpen, setValidateToastOpen] = useState(false)
  const [deactivateFormOpen, setDeactivateFormOpen] = useState(false)
  const [blockFormOpen, setBlockFormOpen] = useState(false)
  const [downloadingDoc, setDownloadingDoc] = useState(null)
  const [deactivateMessage, setDeactivateMessage] = useState('')
  const [deactivatingAgent, setDeactivatingAgent] = useState(false)
  const [deactivateToastOpen, setDeactivateToastOpen] = useState(false)
  const [showValidatedView, setShowValidatedView] = useState(false)

  const {
    agentProperties: properties,
    loading: propertiesLoading,
    error: propertiesError,
  } = useAppSelector(state => state.property)
  const {
    recentActivities,
    gdprConsents,
    loading: activitiesLoading,
    error: activitiesError,
  } = useAppSelector(state => state.activity)
  const {
    agent,
    loading: agentLoading,
    error: agentError,
    fetchAgent,
    approveAgent,
    disapproveAgent,
  } = useAgent(agentId)

  const loading = agentLoading || propertiesLoading
  const error = agentError || propertiesError

  // Fetch property data
  const fetchAgentPropertyData = useCallback(() => {
    dispatch(fetchPropertiesbyAgent(agentId))
  }, [dispatch, agentId])

  useEffect(() => {
    if (!agentId || !/^[a-f\d]{24}$/i.test(agentId)) {
      navigate('/agents') // Redirect to agent list
      return
    }
    fetchAgent(agentId)
    fetchAgentPropertyData()
    // Fetch recent activities for this agent
    dispatch(getRecentActivities({ userType: 'agent', limit: 10 }))
    // Fetch GDPR consents for this agent
    dispatch(getGdprConsents(agentId))
    // eslint-disable-next-line
  }, [agentId])

  // Enhanced download function with proper error handling and authentication
  const handleDownload = async (url, filename, docType) => {
    if (!url) {
      alert(`${docType} is not available.`)
      return
    }

    try {
      setDownloadingDoc(docType)

      // Create a temporary anchor element for download
      const link = document.createElement('a')
      link.style.display = 'none'

      // If it's a relative URL, make it absolute
      const downloadUrl = url.startsWith('http') ? url : `${window.location.origin}${url}`

      // Try to fetch the file with proper headers to maintain authentication
      try {
        const response = await fetch(downloadUrl, {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') || sessionStorage.getItem('token')}`,
            'Content-Type': 'application/octet-stream',
          },
          credentials: 'include', // Include cookies for authentication
        })

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`)
        }

        const blob = await response.blob()
        const blobUrl = window.URL.createObjectURL(blob)

        link.href = blobUrl
        link.download = filename || `${docType}_${agent?.full_name || 'document'}.${getFileExtension(url)}`

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        // Clean up the blob URL
        window.URL.revokeObjectURL(blobUrl)
      } catch (fetchError) {
        console.warn('Fetch method failed, trying direct download:', fetchError)

        // Fallback: Direct download method
        link.href = downloadUrl
        link.download = filename || `${docType}_${agent?.full_name || 'document'}.${getFileExtension(url)}`
        link.target = '_blank'
        link.rel = 'noopener noreferrer'

        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error(`Error downloading ${docType}:`, error)
      alert(`Failed to download ${docType}. Please try again or contact support.`)
    } finally {
      setDownloadingDoc(null)
    }
  }

  // Helper function to get file extension from URL
  const getFileExtension = url => {
    const parts = url.split('.')
    return parts.length > 1 ? parts.pop().split('?')[0] : 'pdf' // Default to pdf if no extension
  }

  // Handle broker card download using the API endpoint
  const handleBrokerCardDownload = async e => {
    e.preventDefault()
    e.stopPropagation()

    if (!agentId) {
      alert('Agent ID is not available.')
      return
    }

    try {
      setDownloadingDoc('Broker Card')

      // Use the admin endpoint to generate and download the broker ID card
      const response = await api.get(`/v1/admin/broker-id-card/${agentId}`, {
        responseType: 'blob', // Important: tells axios to expect binary data
      })

      // Create a blob from the PDF data
      const blob = new Blob([response.data], { type: 'application/pdf' })
      const blobUrl = window.URL.createObjectURL(blob)

      // Create a temporary link to trigger download
      const link = document.createElement('a')
      link.href = blobUrl
      link.download = `broker_id_card_${agent?.full_name || 'agent'}.pdf`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

      // Clean up the blob URL
      window.URL.revokeObjectURL(blobUrl)
    } catch (error) {
      console.error('Error downloading broker ID card:', error)
      alert('Failed to download broker ID card. Please try again or contact support.')
    } finally {
      setDownloadingDoc(null)
    }
  }

  const handleRefresh = () => {
    setCurrentPage(1)
    fetchAgent(agentId)
    fetchAgentPropertyData()
  }

  const handleApprove = async () => {
    console.log(`Approving agent: ${agent?.full_name} (ID: ${agentId})`)
    try {
      await approveAgent(agentId).unwrap()
      setShowValidatedView(true)
      setValidateToastOpen(true)
    } catch (error) {
      console.error('Approval failed:', error)
    }
  }

  const handleDisapprove = async () => {
    console.log(`Disapproving agent: ${agent?.full_name} (ID: ${agentId})`)
    try {
      setDisapproveToastOpen(prev => !prev)
      await dispatch(disapproveAgent(agentId))
    } catch (error) {
      console.error('Disapproval failed:', error)
    }
  }

  const handleDeactivate = async () => {
    if (!deactivateMessage.trim()) {
      alert('Please enter a deactivation message.')
      return
    }

    try {
      setDeactivatingAgent(true)
      await dispatch(
        deactivateAgentAccount({
          userId: agentId,
          message: deactivateMessage.trim(),
        })
      ).unwrap()

      setDeactivateFormOpen(false)
      setDeactivateMessage('')
      setDeactivateToastOpen(true)

      // Refresh agent data to show updated status
      await fetchAgent(agentId)
    } catch (error) {
      console.error('Deactivation failed:', error)
      alert(`Failed to deactivate agent: ${error.message || 'Unknown error'}`)
    } finally {
      setDeactivatingAgent(false)
    }
  }

  const handleChange = e => {
    const value = e.target.value
    setSelectValue(value)
  }

  console.log('properties:', properties)

  return (
    <>
      <MiniHeader title="User Management" />
      <div className="ml-[250px] p-8 w-[1050px] mx-auto my-0">
        {loading && <LoadingIndicator />}
        {!loading && error && <ErrorState error={error} />}
        {!loading && !error && !agent && (
          <Card variant="outlined" className="text-center p-8 border border-border rounded-md max-w-md w-full">
            <Typography variant="body" className=" text-secondary ">
              Cannot find agent with ID: {agentId || 'Invalid ID'}, Check for typos
            </Typography>
          </Card>
        )}
        {!loading && !error && agent && (
          <>
            {/* Header Section */}
            <div className="bg-white rounded-lg p-4 mb-6">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Avatar src={'/assets/images/house.jpg'} alt="profile" className="w-16 h-16 rounded-full" />
                  <div>
                    <h1 className="text-md font-semibold text-gray-900">{agent.full_name}</h1>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-1.5 rounded-md font-medium text-sm"
                    onClick={() => console.log('Export clicked')}
                  >
                    Export
                  </Button>
                  <Button
                    className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-1.5 rounded-md font-medium text-sm"
                    onClick={() => navigate(`/chats/${agentId}`)}
                  >
                    View Chats
                  </Button>
                  <Button
                    className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-1.5 rounded-md font-medium text-sm"
                    onClick={handleApprove}
                  >
                    Validate
                  </Button>
                  <Button
                    className="bg-red-500 hover:bg-red-600 text-white px-4 py-1.5 rounded-md font-medium text-sm"
                    onClick={handleDisapprove}
                  >
                    Disprove
                  </Button>
                </div>
              </div>
            </div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Info</h2>
            {/* Info Section */}
            <div className="bg-white p-6 mb-6">
              <div className="bg-white rounded-lg p-6">
                <div className="grid grid-cols-5 gap-6">
                  <div>
                    <Typography className="text-gray-500 text-sm font-medium mb-1">Name</Typography>
                    <Typography className="text-gray-900 text-sm">{agent?.full_name}</Typography>
                  </div>
                  <div>
                    <Typography className="text-gray-500 text-sm font-medium mb-1">Email</Typography>
                    <Typography className="text-gray-900 text-sm">{agent?.email}</Typography>
                  </div>
                  <div>
                    <Typography className="text-gray-500 text-sm font-medium mb-1">Phone</Typography>
                    <Typography className="text-gray-900 text-sm">{agent?.phone_number}</Typography>
                  </div>
                  <div>
                    <Typography className="text-gray-500 text-sm font-medium mb-1">Specialty</Typography>
                    <Typography className="text-gray-900 text-sm">{agent?.specialty}</Typography>
                  </div>
                  <div>
                    <Typography className="text-gray-500 text-sm font-medium mb-1">ORN</Typography>
                    <Typography className="text-gray-900 text-sm">{agent?.orn}</Typography>
                  </div>
                  <div>
                    <Typography className="text-gray-500 text-sm font-medium mb-1">Years of Experience</Typography>
                    <Typography className="text-gray-900 text-sm">{agent?.years_of_experience} years</Typography>
                  </div>
                  <div>
                    <Typography className="text-gray-500 text-sm font-medium mb-1">BRN</Typography>
                    <Typography className="text-gray-900 text-sm">{agent?.brn}</Typography>
                  </div>
                  <div>
                    <Typography className="text-gray-500 text-sm font-medium mb-1">Language</Typography>
                    <Typography className="text-gray-900 text-sm">
                      {agent?.language?.length === 0 || !agent?.language
                        ? 'English, Arabic, Hindka'
                        : agent?.language?.join(', ')}
                    </Typography>
                  </div>
                  <div>
                    <Typography className="text-gray-500 text-sm font-medium mb-1">Company</Typography>
                    <Typography className="text-gray-900">{agent?.company || 'Redhorse Group'}</Typography>
                  </div>
                  <div>
                    <Typography className="text-gray-500 text-sm font-medium mb-1">Company Address</Typography>
                    <Typography className="text-gray-900">
                      {agent?.company_address || 'Building P, city walk, Dubai'}
                    </Typography>
                  </div>
                </div>
              </div>
            </div>

            {/* deactivate form */}
            {deactivateFormOpen && (
              <Backdrop isOpen={deactivateFormOpen} onClose={() => setDeactivateFormOpen(false)}>
                <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
                  <div className="mb-4">
                    <Typography variant="h3" className="mb-2">
                      <strong>Deactivate Agent Account</strong>
                    </Typography>
                    <Typography variant="body" className="text-secondary mb-4">
                      Are you sure you want to deactivate {agent?.full_name}'s account? Please provide a reason for
                      deactivation.
                    </Typography>
                  </div>

                  <div className="mb-4">
                    <textarea
                      value={deactivateMessage}
                      onChange={e => setDeactivateMessage(e.target.value)}
                      placeholder="Enter deactivation reason/message..."
                      className="w-full p-3 border border-gray-300 rounded-md resize-none min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                      disabled={deactivatingAgent}
                    />
                  </div>

                  <div className="flex gap-3 justify-end">
                    <Button
                      variant="secondary"
                      onClick={() => {
                        setDeactivateFormOpen(false)
                        setDeactivateMessage('')
                      }}
                      disabled={deactivatingAgent}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="danger"
                      onClick={handleDeactivate}
                      disabled={deactivatingAgent || !deactivateMessage.trim()}
                    >
                      {deactivatingAgent ? 'Deactivating...' : 'Deactivate Account'}
                    </Button>
                  </div>
                </div>
              </Backdrop>
            )}

            {/* block form */}
            {blockFormOpen && (
              <Backdrop open={blockFormOpen} onClose={() => setBlockFormOpen(prev => !prev)} data={agent}>
                <FeedbackPopup title="New Message" onClose={() => setBlockFormOpen(prev => !prev)} data={agent}>
                  <Button>
                    <Typography>Send and Block</Typography>
                    <MdOutlineArrowDropDown />
                  </Button>
                </FeedbackPopup>
              </Backdrop>
            )}

            {/* deactivate success toast */}
            {deactivateToastOpen && (
              <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center">
                <div className="flex justify-center items-center">
                  <Card className="p-8 justify-center items-center" size={96}>
                    <div className="flex gap-2 justify-center items-center mb-4">
                      <MdOutlineVerified className="text-primary text-4xl" />
                      <Typography>Account Successfully Deactivated</Typography>
                    </div>
                    <div className="flex px-8 justify-center items-center">
                      <Button size="medium" onClick={() => setDeactivateToastOpen(false)}>
                        Done
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>
            )}

            <h2 className="text-lg font-medium text-gray-900 mb-2 bg-gray-50/10 px-4 py-2 rounded-md">Description</h2>
            {/* Description Section */}
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              <div className="p-6">
                <p className="text-sm text-gray-700 leading-relaxed">
                  Lorem ipsum dolor sit amet consectetur. Dui sit vestibulum ipsum placerat cursus mauris. Duis sit
                  felis et varius vitae. Commodo ac in lectus cursus placerat et sed tristique nunc. Adipiscing gravida
                  pellentesque sed mauris nunc odorque turpis. Vitae sed integer porttitor ut imperdiet et sit tristique
                  eget. Tristique enim ac ut lobortis. Vitae lobortis et venenatis consectetur porttitor ut lorem.
                </p>
              </div>
            </div>
            {!showValidatedView && (
              <>
                <h2 className="text-lg font-medium text-gray-900 mb-2  px-4 py-2 rounded-md">Identity</h2>
                {/* Identity Section */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                  <div className="p-6">
                    <div className="grid grid-cols-3 gap-8">
                      <div>
                        <div className="text-sm font-medium text-gray-500 mb-1">Broker ID</div>
                        <div className="text-sm text-gray-900">{agent?.brn || '3510002'}</div>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-500 mb-1">Front of broker id card</div>
                        <Button
                          variant="primary"
                          size="small"
                          className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-1 rounded-md text-xs flex items-center gap-1"
                          onClick={handleBrokerCardDownload}
                          disabled={downloadingDoc === 'Broker Card'}
                        >
                          <RiDownloadCloud2Fill className="w-3 h-3" />
                          {downloadingDoc === 'Broker Card' ? 'Downloading...' : 'Download'}
                        </Button>
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-500 mb-1">Back of broker id card</div>
                        <Button
                          variant="primary"
                          size="small"
                          className="bg-teal-500 hover:bg-teal-600 text-white px-4 py-1 rounded-md text-xs flex items-center gap-1"
                        >
                          <RiDownloadCloud2Fill className="w-3 h-3" />
                          Download
                        </Button>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* GDPR Section */}
            <h2 className="text-lg font-medium text-gray-900 mb-2 px-4 py-2 rounded-md">GDPR</h2>
            {activitiesLoading ? (
              <div className="px-6 py-8 text-center">
                <LoadingIndicator />
              </div>
            ) : activitiesError ? (
              <div className="px-6 py-8">
                <ErrorState message={activitiesError} />
              </div>
            ) : gdprConsents && gdprConsents.length > 0 ? (
              <div className="grid grid-cols-2 gap-4 mb-6">
                {gdprConsents.map(notification => (
                  <div key={notification._id} className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
                    <h3 className="text-sm font-semibold text-gray-900 mb-2">
                      {notification.title || 'GDPR Notification'}
                    </h3>
                    <span
                      className={`inline-block px-3 py-1 text-xs font-medium text-white rounded mb-2 ${
                        notification.status === 'Positive' ? 'bg-[#36b915]' : 'bg-red-500'
                      }`}
                    >
                      {notification.status || 'Pending'}
                    </span>
                    <p className="text-xs text-gray-600 mb-3">{notification.agreedOn || 'N/A'}</p>
                    <p className="text-xs text-gray-600 mb-3">{notification.message || 'No message'}</p>
                    <p className="text-xs text-gray-600">{notification.version || 'N/A'}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="px-6 py-8 text-center text-gray-500">
                <p>No GDPR notifications available</p>
              </div>
            )}

            {/* Legacy description section - keeping for compatibility */}
            <div className="hidden py-4">
              <Typography variant="h3" className="mb-2">
                <strong>Description</strong>
              </Typography>
              <div className="bg-white px-8 py-8">
                <Typography>{agent?.description}</Typography>
              </div>
            </div>
            {/* activity */}
            {showValidatedView && <AgentActivityTabs handleRefresh={handleRefresh} agent={agent} />}
            {/* toast */}
            {validateToastOpen && (
              <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center">
                <div className="flex justify-center items-center">
                  <Card className="p-8 justify-center items-center" size={96}>
                    <div className="flex gap-2 justify-center items-center">
                      <MdOutlineVerified className="text-primary text-4xl" />
                      <Typography>Successfully Validated</Typography>
                    </div>
                    <div className="flex px-8 justify-center items-center">
                      <Button size="medium" onClick={() => setValidateToastOpen(prev => !prev)}>
                        Done
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>
            )}
            {disapproveToastOpen && (
              <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center">
                <div className="flex justify-center items-center">
                  <Card className="p-8 justify-center items-center" size={96}>
                    <div className="flex gap-2 justify-center items-center">
                      <MdOutlineVerified className="text-primary text-4xl" />
                      <Typography>Disapproval Sent</Typography>
                    </div>
                    <div className="flex px-8 justify-center items-center">
                      <Button size="medium" onClick={() => setDisapproveToastOpen(prev => !prev)}>
                        Done
                      </Button>
                    </div>
                  </Card>
                </div>
              </div>
            )}

            {/* Recent Activity Section */}
            <h2 className="text-lg font-medium text-gray-900 mb-4 px-4 py-2 rounded-md">Recent Activity</h2>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
              {/* <div className="px-6 pt-6">
                <h3 className="text-md font-medium text-gray-900 mb-4">Recent Activities</h3>
              </div> */}
              {activitiesLoading ? (
                <div className="px-6 py-8 text-center">
                  <LoadingIndicator />
                </div>
              ) : activitiesError ? (
                <div className="px-6 py-8">
                  <ErrorState message={activitiesError} />
                </div>
              ) : recentActivities && recentActivities.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          DEVICE
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          ACTIVITY
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          IP ADDRESS
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          LOCATION
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          RECENT ACTIVITY
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {recentActivities.map((activity, index) => (
                        <tr key={activity._id || index}>
                          <td className="px-4 py-3 text-sm text-gray-900">{activity.device || 'Unknown Device'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900 capitalize">
                            {activity.activity?.replace(/_/g, ' ') || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">{activity.ipAddress || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{activity.location || 'N/A'}</td>
                          <td className="px-4 py-3 text-sm text-gray-900">{activity.recentActivity || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="px-6 py-8 text-center text-gray-500">
                  <p>No recent activities found</p>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </>
  )
}

export default AgentProfile
